/** The board's shared panel chrome: an outlined card with a title and caption.
 *
 *  Two different footers, deliberately distinguished. `verdict` is the executive
 *  deck's card grammar — one short status sentence saying what the panel means
 *  (`components/board/verdict.tsx`), rendered the same way on every board. `note`
 *  is the methodology small print: how a floor is computed, what a count counts,
 *  what is deliberately not stored. A reader takes the verdict; a reader who
 *  challenges the verdict takes the note.
 */

import { Card, Typography } from "@norican/react";
import type { ReactNode } from "react";

import { type Verdict, VerdictFooter } from "@/components/board";

interface PanelCardProps {
  /** Panel heading. */
  title: string;
  /** Muted line beside the heading — a count, a window, a caveat. */
  caption?: ReactNode;
  /** Panel content. */
  children: ReactNode;
  /** Body padding. `none` for content that brings its own. */
  padding?: "none" | "sm" | "md" | "lg";
  /** The panel's closing status verdict, in the deck's voice. */
  verdict?: Verdict;
  /** Methodology and privacy small print, under the verdict. */
  note?: ReactNode;
}

export function PanelCard({
  title,
  caption,
  children,
  padding = "md",
  verdict,
  note,
}: PanelCardProps) {
  return (
    <Card variant="outline">
      <Card.Header>
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <Typography variant="heading-4" as="h3">
            {title}
          </Typography>
          {caption && (
            <Typography variant="body-sm" tone="muted">
              {caption}
            </Typography>
          )}
        </div>
      </Card.Header>
      <Card.Body padding={padding}>{children}</Card.Body>
      {(verdict || note) && (
        <Card.Footer>
          <div className="flex flex-col gap-2">
            {verdict && <VerdictFooter verdict={verdict} />}
            {note}
          </div>
        </Card.Footer>
      )}
    </Card>
  );
}
